import type { UserMaterial } from '@/lib/api/materials';
import type { CustomStyle } from '@/types/custom-style';
import type { SubjectModel } from '@/types/subject-model';

function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  const deduped: T[] = [];
  for (const item of items) {
    const id = String(item.id || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    deduped.push(item);
  }
  return deduped;
}

export function dedupeSubjectModels(models: SubjectModel[]): SubjectModel[] {
  const statusPriority: Record<SubjectModel['analysis_status'], number> = {
    completed: 0,
    analyzing: 1,
    pending: 2,
    failed: 3,
  };
  const deduped: SubjectModel[] = [];
  const indexByIdentity = new Map<string, number>();

  for (const model of dedupeById(models)) {
    const anchorAssetIds = model.anchor_images
      .map((anchor) => String(anchor.asset_id || '').trim())
      .filter(Boolean);
    const identity = model.character_kind === 'soul_id' ? `soul:${model.id}` : model.product_kind === 'krea_lora' ? `krea:${model.id}` : anchorAssetIds.length === model.anchor_images.length && anchorAssetIds.length > 0
      ? `${model.model_type}:${anchorAssetIds.join('|')}`
      : `id:${model.id}`;
    const existingIndex = indexByIdentity.get(identity);
    if (existingIndex === undefined) {
      indexByIdentity.set(identity, deduped.length);
      deduped.push(model);
      continue;
    }

    const existing = deduped[existingIndex];
    const candidatePriority = statusPriority[model.analysis_status];
    const existingPriority = statusPriority[existing.analysis_status];
    if (
      candidatePriority < existingPriority
      || (
        candidatePriority === existingPriority
        && Date.parse(model.updated_at) > Date.parse(existing.updated_at)
      )
    ) {
      deduped[existingIndex] = model;
    }
  }

  return deduped;
}

export function dedupeCustomStyles(styles: CustomStyle[]): CustomStyle[] {
  return dedupeById(styles);
}

export function dedupeUserMaterials(materials: UserMaterial[]): UserMaterial[] {
  return dedupeById(materials);
}
